import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { path } from "../data/base";
import { buildKillPayload, type MachineStatus } from "../data/cockpit-data";

/**
 * The kill switch, and the sentence that says what it actually does.
 *
 * In Meteoric this lived in the cockpit's own purple header bar. The portal
 * owns the chrome (I-7), so the control moves INTO the Configuration tab —
 * beside the kill state it changes, which is where a reader looking for it
 * goes. Nothing about the confirm flow or the write changes.
 *
 * The two lines in the dialog are the whole point of the dialog: "OFF" reads
 * like "stop the ads", and it is not — live campaigns keep serving, and only
 * the next commit cycle is blocked. Someone who kills the machine expecting
 * spend to stop has been misled by the word, not by the button.
 */
export function KillToggle({
  initialState,
  onChanged,
}: {
  initialState: MachineStatus;
  onChanged?: () => void;
}) {
  const [state, setState] = useState<MachineStatus>(initialState);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const nextState: MachineStatus = state === "ON" ? "OFF" : "ON";

  async function submit() {
    setBusy(true);
    setMessage(null);
    try {
      const response = await fetch(path("/config"), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(
          buildKillPayload(
            nextState,
            nextState === "OFF" ? "Operator kill from cockpit" : "Operator resume from cockpit",
          ),
        ),
      });
      // Only Damien holds go-live authority, and that is enforced upstream. The
      // module states the refusal rather than keeping a second copy of the rule.
      if (response.status === 403) {
        setMessage("You don't have go-live authority.");
        return;
      }
      if (!response.ok) {
        setMessage("Machine status write failed.");
        return;
      }
      setState(nextState);
      setOpen(false);
      setMessage(nextState === "OFF" ? "Machine OFF. Next cycle blocked." : "Machine LIVE.");
      onChanged?.();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Badge variant={state === "ON" ? "secondary" : "outline"}>
        {state === "ON" ? "LIVE" : "MACHINE OFF"}
      </Badge>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        {state === "ON" ? "Kill" : "Resume"}
      </Button>
      {message && (
        <span className="text-xs text-muted-foreground" role="status">
          {message}
        </span>
      )}

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next && busy) return;
          setOpen(next);
        }}
      >
        <DialogContent aria-describedby={undefined} className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {nextState === "OFF" ? "Turn the machine OFF?" : "Turn the machine LIVE?"}
            </DialogTitle>
          </DialogHeader>
          <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
            <li>Blocks the NEXT commit cycle.</li>
            <li>Live campaigns KEEP serving.</li>
          </ul>
          <p className="text-sm font-semibold text-ks-ink">Blocks next cycle. Ads keep serving.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={busy}>
              {busy ? "Saving…" : nextState === "OFF" ? "Turn OFF" : "Turn LIVE"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
